import { ColDef, RowClickedEvent, SortChangedEvent } from 'ag-grid-community';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Dispatch, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingRespondent, PendingRespondentStatusMap, SearchParams, SortModelItem } from 'src/@types/pendingRespondentStatus';
import { usePendingRespondentApi } from 'src/api/usePendingRespondentApi';
import useLocales from 'src/hooks/useLocales';
import { switchLabel } from 'src/utils/labelUtils';
import { Action, actions } from '../utils/searchState';

type UseIdIssuanceManagementGridProps = {
  statusMap: PendingRespondentStatusMap;
  setSelectedUser: (data: PendingRespondent | undefined) => void;
  fetchPendingRespondents: (searchParams: SearchParams) => Promise<void>;
  searchParams: SearchParams;
  dispatch: Dispatch<Action>;
};

export const useIdIssuanceManagementGrid = ({
  statusMap,
  setSelectedUser,
  fetchPendingRespondents,
  searchParams,
  dispatch,
}: UseIdIssuanceManagementGridProps) => {
  const { t } = useTranslation();
  const { getPendingRespondentById } = usePendingRespondentApi();
  const { currentLang } = useLocales();
  const isLangJpn = currentLang.value === 'ja';

  const onRowClicked = async (event: RowClickedEvent<PendingRespondent>) => {
    if (event.data && event.data.status === 'AGREED') {
      const response = await getPendingRespondentById(event.data.id);
      if (response) {
        setSelectedUser({ ...event.data, emailTemplate: response.item.emailTemplate });
        return;
      }
    }
    setSelectedUser(event.data);
  };

  const onSortChanged = async (event: SortChangedEvent): Promise<void> => {
    const newSortModel = event.columnApi
      .getColumnState()
      .filter((col) => col.sort)
      .map((col) => ({
        colId: col.colId,
        sort: col.sort,
      }));
    dispatch(actions.setSortModel(newSortModel));
    await fetchPendingRespondents({ ...searchParams, sortModel: newSortModel });
  };

  const columnDefs: ColDef<PendingRespondent>[] = [
    {
      field: 'email',
      headerName: t('idIssuanceManagement.grid.gridColDef.email'),
      minWidth: 200,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === 'email')?.sort,
      sortable: true,
      cellStyle: (params) => {
        return {
          color: params.data?.canDelete ? '#d32f2f' : 'inherit',
        };
      },
    },
    {
      field: isLangJpn ? 'userLastNameJpn' : 'userFirstNameEng',
      headerName: t('idIssuanceManagement.grid.gridColDef.name'),
      minWidth: 200,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === (isLangJpn ? 'userLastNameJpn' : 'userFirstNameEng'))?.sort,
      sortable: true,
      valueGetter: (params) => {
        const nameJpn =
          params.data?.userLastNameJpn && params.data?.userFirstNameJpn ? `${params.data?.userLastNameJpn} ${params.data?.userFirstNameJpn}` : '';
        const nameENg =
          params.data?.userFirstNameEng && params.data?.userLastNameEng ? `${params.data?.userFirstNameEng} ${params.data?.userLastNameEng}` : '';
        return switchLabel(nameJpn, nameENg, isLangJpn);
      },
    },
    {
      field: isLangJpn ? 'companyNameJpn' : 'companyNameEng',
      headerName: t('idIssuanceManagement.grid.gridColDef.companyName'),
      minWidth: 250,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === (isLangJpn ? 'companyNameJpn' : 'companyNameEng'))?.sort,
      sortable: true,
      valueGetter: (params) => switchLabel(params.data?.companyNameJpn ?? '', params.data?.companyNameEng ?? '', isLangJpn),
      comparator(valueA, valueB) {
        return String(valueA).localeCompare(String(valueB), 'ja-Jp', { sensitivity: 'base', ignorePunctuation: false });
      },
    },
    {
      field: 'respondentRequestId',
      headerName: t('idIssuanceManagement.grid.gridColDef.enqueteId'),
      minWidth: 250,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === 'respondentRequestId')?.sort,
      sortable: true,
    },
    {
      field: 'status',
      headerName: t('idIssuanceManagement.grid.gridColDef.status'),
      minWidth: 120,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === 'status')?.sort,
      sortable: true,
      valueFormatter: (params) => {
        return statusMap[params.value as PendingRespondent['status']] || params.value;
      },
    },
    {
      field: 'updatedAt',
      headerName: t('idIssuanceManagement.grid.gridColDef.updatedAt'),
      minWidth: 150,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === 'updatedAt')?.sort,
      sortable: true,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'yyyy/MM/dd HH:mm', { locale: ja });
      },
    },
    {
      field: 'expiresAt',
      headerName: t('idIssuanceManagement.grid.gridColDef.expiresAt'),
      minWidth: 150,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === 'expiresAt')?.sort,
      sortable: true,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'yyyy/MM/dd HH:mm', { locale: ja });
      },
    },
    {
      field: 'sentAt',
      headerName: t('idIssuanceManagement.grid.gridColDef.sentAt'),
      minWidth: 150,
      sort: searchParams.sortModel.find((model: SortModelItem) => model.colId === 'sentAt')?.sort,
      sortable: true,
      valueFormatter: (params) => {
        return params.value && format(new Date(params.value), 'yyyy/MM/dd HH:mm', { locale: ja });
      },
    },
  ];

  const defaultColDef: ColDef<PendingRespondent> = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  const onGridReady = useCallback(() => {
    fetchPendingRespondents(searchParams);
  }, [fetchPendingRespondents, searchParams]);

  return {
    onRowClicked,
    onSortChanged,
    onGridReady,
    columnDefs,
    defaultColDef,
  };
};
